import { useGetList, type Identifier } from "ra-core";
import { useMemo } from "react";

import type { Sale } from "@/components/atomic-crm/types";

import type {
  ContentItem,
  ContentMetric,
  ContentPlan,
  ContentPlanChannel,
  ContentPlanPhase,
  ContentPlanPillar,
  MarketingChannel,
  MarketingData,
} from "./types";

const ALL = { pagination: { page: 1, perPage: 5000 } } as const;

/**
 * Every marketing table, loaded once (small tables) and shared through the
 * react-query cache by every section.
 */
export const useMarketingData = (): {
  data: MarketingData;
  isPending: boolean;
  error: unknown;
} => {
  const channels = useGetList<MarketingChannel>("marketing_channels", {
    ...ALL,
    sort: { field: "id", order: "ASC" },
  });
  const plans = useGetList<ContentPlan>("content_plans", {
    ...ALL,
    sort: { field: "start_date", order: "DESC" },
  });
  const pillars = useGetList<ContentPlanPillar>("content_plan_pillars", {
    ...ALL,
    sort: { field: "position", order: "ASC" },
  });
  const phases = useGetList<ContentPlanPhase>("content_plan_phases", {
    ...ALL,
    sort: { field: "number", order: "ASC" },
  });
  const cadences = useGetList<ContentPlanChannel>("content_plan_channels", {
    ...ALL,
    sort: { field: "id", order: "ASC" },
  });
  const items = useGetList<ContentItem>("content_items", {
    ...ALL,
    sort: { field: "scheduled_at", order: "ASC" },
  });
  const metrics = useGetList<ContentMetric>("content_metrics", {
    ...ALL,
    sort: { field: "week_start", order: "ASC" },
  });
  const all = [channels, plans, pillars, phases, cadences, items, metrics];
  const data = useMemo<MarketingData>(
    () => ({
      channels: channels.data ?? [],
      plans: plans.data ?? [],
      pillars: pillars.data ?? [],
      phases: phases.data ?? [],
      cadences: cadences.data ?? [],
      items: items.data ?? [],
      metrics: metrics.data ?? [],
    }),
    [
      channels.data,
      plans.data,
      pillars.data,
      phases.data,
      cadences.data,
      items.data,
      metrics.data,
    ],
  );
  return {
    data,
    isPending: all.some((query) => query.isPending),
    error: all.find((query) => query.error)?.error ?? null,
  };
};

/** Active team members, for the assignee select and avatars. */
export const useSales = (): Sale[] => {
  const { data } = useGetList<Sale>("sales", {
    ...ALL,
    sort: { field: "first_name", order: "ASC" },
  });
  return data ?? [];
};

export const findById = <T extends { id: Identifier }>(
  rows: readonly T[],
  id: Identifier | null | undefined,
): T | undefined =>
  id == null ? undefined : rows.find((row) => String(row.id) === String(id));

export const saleName = (sale: Sale | undefined): string =>
  sale ? `${sale.first_name} ${sale.last_name}`.trim() : "";
